import { RootState } from '@src/store/store';

import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';
import { mockResponse } from '@src/_mock/user';

import { logOut } from '../auth/auth-slice';

const URL =
  import.meta.env.MODE === 'production'
    ? import.meta.env.VITE_BASE_URL_PROD
    : import.meta.env.VITE_BASE_URL_DEV;

export const baseQuery = fetchBaseQuery({
  baseUrl: URL,
  credentials: 'include',
  prepareHeaders: (headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.token;
    if (accessToken) {
      headers.set('authorization', `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export const baseQueryWithoutReauth: BaseQueryFn<
  QueryArgs<any>,
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result?.error?.status === 401) {
    api.dispatch(logOut());
  }

  return result;
};

export const baseQueryWithoutJwt = fetchBaseQuery({
  baseUrl: URL,
});

type QueryArgs<BodyType = any> = {
  url: string;
  method?: string;
  body?: BodyType; // Generic body type
};

const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(() => resolve(), ms);
  });

export const baseQueryWithMock: BaseQueryFn<
  QueryArgs<any>,
  unknown,
  unknown
> = async (args, api, extraOptions) => {
  const { url, body } = args;

  const authEndpoint = url.startsWith('/auth');

  if (authEndpoint) {
    // Simulate a delay of 2 seconds
    await delay(2000);

    // Use mockResponse to get mock data based on the request URL and body
    const mockData = body && mockResponse(url, body);

    if (mockData) {
      return { data: mockData };
    }

    // Handle the case when the endoipnt does not match
    return { error: { status: 404, message: 'Mock endpoint not found' } };
  }

  return baseQueryWithoutJwt(args, api, extraOptions);
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithoutReauth,
  endpoints: () => ({}),
});
